import { ApiError } from '~/api/errors';
import { useCanChooseFileLayout } from '~/hooks/useCanChooseFileLayout';

import type { ResolvedTransformer, TransformerConnector } from '../types/transformer.types';
import { isStandardResolution } from '../utils/transformer-display';

import { useResolveTransformers, useTransformers } from './use-transformers';

/** `pickedId` while "use another file transformer" is selected but nothing is chosen. */
export const PICK_PENDING = '';

interface Params {
  subsidiaryId: string;
  connector: TransformerConnector;
  /** SFTP connection for deliveries; omitted for downloads. */
  connectionId?: string | undefined;
  /**
   * Transformer a Lily user picked instead of the resolved one(s); null = use
   * resolution; `PICK_PENDING` ('') = "use another" chosen but no transformer yet.
   */
  pickedId: string | null;
  enabled: boolean;
}

/**
 * Everything a File layout control and its host need: what resolve says applies,
 * whether a Lily user's pick overrides it, and whether the transformer path is in
 * force at all (anything but the standard export) — when it isn't, the host keeps
 * its existing template / columns path.
 */
export function useFileLayout({
  subsidiaryId,
  connector,
  connectionId,
  pickedId,
  enabled,
}: Params) {
  const canChoose = useCanChooseFileLayout();
  const resolve = useResolveTransformers(
    enabled && subsidiaryId ? { subsidiaryId, connector, connectionId } : null,
  );
  // The library read is open to Lily users only; customers never see the picker.
  const library = useTransformers({ connector }, enabled && canChoose);
  // A Lily user's pick resolved on its own (`transformerId`, reason "explicit") so the
  // dialogs can show the file names it would write instead of the assigned ones.
  const picking = canChoose && pickedId !== null;
  // "Use another" with nothing chosen yet: hosts block submit rather than guess one.
  const pickPending = picking && pickedId === PICK_PENDING;
  const explicit = useResolveTransformers(
    enabled && subsidiaryId && picking && !pickPending
      ? { subsidiaryId, connector, connectionId, transformerId: pickedId }
      : null,
  );

  // Resolve needs DELIVERIES:READ. A 403 means this user can't see layouts at all, so
  // the standard path stands. Any other failure (5xx / 503 / network) must NOT fall
  // back silently: the subsidiary may have an assigned layout, and exporting the
  // standard file instead would hand the customer the wrong file. Hosts block on it.
  const forbidden = resolve.error instanceof ApiError && resolve.error.statusCode === 403;
  const failed = resolve.isError && !forbidden;

  const resolved: ResolvedTransformer[] = resolve.data?.transformers ?? [];
  const standard = resolved.length > 0 && resolved.every((r) => isStandardResolution(r));
  const picked = picking ? library.data?.find((tr) => tr.id === pickedId) : undefined;
  // The transformer path applies when a Lily user picked one, or resolve found an assignment.
  const applies = picking || (resolved.length > 0 && !standard);
  const format = picked?.format ?? resolved[0]?.format;
  // Only trust an explicit answer about this pick (an older integrations ignores the param).
  const explicitItem = explicit.data?.transformers.find(
    (r) => r.reason === 'explicit' && r.transformerId === pickedId,
  );
  const assigned = resolved.filter((r) => !isStandardResolution(r));
  /** File names this export / delivery writes; `[]` on the standard path or when unknown. */
  const fileNames = picking
    ? (explicitItem?.fileNames ?? [])
    : assigned.flatMap((r) => r.fileNames);
  /** The transformer to open in the editor: the pick, else the (first) assigned one. */
  const transformerId = picking ? pickedId : (assigned[0]?.transformerId ?? null);
  /** Display name(s) of the layout in force, for toasts and summaries. */
  const layoutName = picking
    ? (picked?.name ?? explicitItem?.name ?? '')
    : assigned.map((r) => r.name).join(', ');

  // Two transformers on one connection must not write the same file name.
  const names = resolved.flatMap((r) => r.fileNames);
  const clashes = names.filter((n, i) => names.indexOf(n) !== i);

  return {
    canChoose,
    resolved,
    standard,
    picked,
    applies,
    format,
    clashes: picking ? [] : [...new Set(clashes)],
    fileNames,
    /** The explicit resolve for a pick is still loading its file names. */
    fileNamesLoading: picking && !pickPending && explicit.isLoading,
    /** "Use another" is selected but no transformer is chosen yet — hosts block submit. */
    pickPending,
    transformerId,
    layoutName,
    library: library.data ?? [],
    isLoading: resolve.isLoading,
    /** Resolve was refused (403): no layout is visible to this user — standard export. */
    forbidden,
    /** Resolve failed for any other reason — hosts must block until a retry succeeds. */
    failed,
    isRetrying: resolve.isFetching,
    retry: () => {
      void resolve.refetch();
    },
  };
}

export type FileLayoutState = ReturnType<typeof useFileLayout>;
